import React from 'react';
import { Box, Typography } from '@mui/material';
import Markdown from 'markdown-to-jsx';
import { LABOKLIN_BORDER } from '../../theme/laboklinTheme';

/**
 * One block of the diagnostic-support column, and the small caps label above it.
 *
 * Shared so the three panels sit on the same rhythm; there is nothing clever here.
 */

export const PanelEyebrow: React.FC<{ label: string; state?: string }> = ({ label, state }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      mb: 1,
      gap: 1,
    }}
  >
    <Typography
      sx={{
        fontSize: 11,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        color: 'text.secondary',
        fontWeight: 600,
      }}
    >
      {label}
    </Typography>
    {state && (
      <Typography sx={{ fontSize: 11, color: 'text.secondary', textAlign: 'right' }}>
        {state}
      </Typography>
    )}
  </Box>
);

/**
 * The assistant's prose, with its markdown rendered rather than printed.
 *
 * These strings are written by a model into a JSON field, and models write markdown -
 * so a section listing three findings arrived as "- **Potassium: 35.8 mmol/l** - above
 * 10 mmol/l..." and rendered with the asterisks and hyphens visible, run together on
 * one line. On a panel whose whole job is to be more readable than a chat transcript,
 * that is worse than the transcript.
 *
 * Inline by default: the panel supplies the structure - a run-in bold title, then the
 * prose - so a paragraph must not open a new block and break that line. A list is the
 * exception, because where the assistant did write one it is because there were three
 * separate findings, and running them together is what caused this.
 */
export const Prose: React.FC<{ children: string }> = ({ children }) => (
  <Markdown
    options={{
      forceInline: true,
      overrides: {
        // Tight, and matching the surrounding type rather than the browser default.
        ul: { props: { style: { margin: '4px 0 0', paddingLeft: '18px' } } },
        ol: { props: { style: { margin: '4px 0 0', paddingLeft: '18px' } } },
        li: { props: { style: { marginBottom: '2px' } } },
        strong: { props: { style: { fontWeight: 600 } } },
        p: { props: { style: { margin: 0, display: 'inline' } } },
        // The panel owns its own headings; one written into a section would compete
        // with the run-in title beside it.
        h1: 'strong',
        h2: 'strong',
        h3: 'strong',
        h4: 'strong',
      },
    }}
  >
    {children}
  </Markdown>
);

const PanelBlock: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ px: 2.5, py: 2.25, borderBottom: `1px solid ${LABOKLIN_BORDER}`, '&:last-of-type': { borderBottom: 0 } }}>
    {children}
  </Box>
);

export default PanelBlock;
